import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Phone, RefreshCw, AlertCircle, Users, TrendingUp, HandCoins } from 'lucide-react';
import { fmtMoney as fmt } from '../../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isoToday() { return new Date().toISOString().split('T')[0]; }
function isoMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function ReporteCuentasPorCobrar() {
  const navigate = useNavigate();
  const [fechaInicio, setFechaInicio] = useState(isoMonthStart);
  const [fechaFin, setFechaFin] = useState(isoToday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const fetchReporte = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await axios.get(`${API}/reportes/cuentas-por-cobrar`, {
        params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin },
      });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { fetchReporte(); }, [fetchReporte]);

  const resumen = data?.resumen || {};
  const clientes = (data?.clientes || []).filter(c => {
    if (!busqueda) return true;
    return c.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      (c.telefono || '').includes(busqueda);
  });

  const conSaldo = clientes.filter(c => c.saldo_actual < 0);
  const sinSaldo = clientes.filter(c => c.saldo_actual >= 0 && (c.total_credito_periodo > 0 || c.total_abonado_periodo > 0));

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-zinc-900">Cuentas por Cobrar</h1>
            <p className="text-xs text-zinc-500">Saldo de clientes a crédito</p>
          </div>
          <button
            onClick={fetchReporte}
            disabled={loading}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg text-zinc-500"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4 pb-24">
        {/* Date range */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <p className="text-sm font-medium text-zinc-600 mb-3">Período de créditos y abonos</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Desde</label>
              <input
                type="date"
                value={fechaInicio}
                max={fechaFin}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-medium"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Hasta</label>
              <input
                type="date"
                value={fechaFin}
                min={fechaInicio}
                max={isoToday()}
                onChange={(e) => setFechaFin(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-medium"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-20">
            <span className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
          </div>
        )}

        {data && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-red-500" />
                  <span className="text-xs font-medium text-red-600 uppercase">Por cobrar</span>
                </div>
                <p className="text-2xl font-bold text-red-700">{fmt(resumen.total_por_cobrar)}</p>
                <p className="text-xs text-red-500 mt-0.5">{resumen.num_clientes_con_saldo} cliente{resumen.num_clientes_con_saldo !== 1 ? 's' : ''} con saldo</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-blue-600 uppercase">Crédito período</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{fmt(resumen.total_credito_periodo)}</p>
                <p className="text-xs text-blue-500 mt-0.5">otorgado en el período</p>
              </div>
              <div className="col-span-2 bg-green-50 border border-green-200 rounded-xl p-4 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <HandCoins className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Abonado en el período</span>
                </div>
                <span className="text-xl font-bold text-green-700">{fmt(resumen.total_abonado_periodo)}</span>
              </div>
            </div>

            {/* Search */}
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre o teléfono..."
              className="w-full h-11 px-4 border border-zinc-300 rounded-xl text-sm bg-white focus:outline-none focus:border-zinc-500"
            />

            {/* Clients with outstanding balance */}
            {conSaldo.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2 px-1">
                  Con saldo pendiente ({conSaldo.length})
                </p>
                <div className="space-y-2">
                  {conSaldo.map((c) => (
                    <ClienteCard key={c.cliente_id} cliente={c} />
                  ))}
                </div>
              </div>
            )}

            {/* Clients with no balance but had activity */}
            {sinSaldo.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2 px-1">
                  Saldo liquidado – con movimientos en el período ({sinSaldo.length})
                </p>
                <div className="space-y-2">
                  {sinSaldo.map((c) => (
                    <ClienteCard key={c.cliente_id} cliente={c} liquidado />
                  ))}
                </div>
              </div>
            )}

            {conSaldo.length === 0 && sinSaldo.length === 0 && (
              <div className="bg-white rounded-xl border border-zinc-200 p-8 text-center text-zinc-400">
                <p className="font-medium">Sin resultados</p>
                <p className="text-sm mt-1">No hay clientes con movimientos en este período</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const fmtFecha = (f) => {
  if (!f) return '';
  const [y, m, d] = f.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

function ClienteCard({ cliente: c, liquidado }) {
  const saldoPendiente = Math.abs(Math.min(c.saldo_actual, 0));
  const abonos = c.abonos_detalle || [];

  return (
    <div className={`bg-white rounded-xl border overflow-hidden ${liquidado ? 'border-zinc-200' : 'border-red-200'}`}>
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between ${liquidado ? 'bg-zinc-50' : 'bg-red-50'}`}>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-zinc-900 truncate">{c.nombre}</p>
          {c.telefono && (
            <a href={`tel:${c.telefono}`} className="text-xs text-zinc-500 flex items-center gap-1">
              <Phone className="w-3 h-3" /> {c.telefono}
            </a>
          )}
        </div>
        <div className="text-right shrink-0 ml-3">
          {liquidado ? (
            <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Liquidado</span>
          ) : (
            <>
              <p className="text-xs text-red-500 font-medium">Saldo pendiente</p>
              <p className="text-xl font-bold text-red-700">{fmt(saldoPendiente)}</p>
            </>
          )}
        </div>
      </div>

      {/* Crédito vs abonado */}
      <div className="px-4 pt-3 pb-2 grid grid-cols-2 gap-2 text-sm border-b border-zinc-100">
        <div>
          <p className="text-xs text-zinc-500">Crédito período</p>
          <p className="font-semibold text-blue-700">{fmt(c.total_credito_periodo)}</p>
          <p className="text-xs text-zinc-400">{c.num_ventas} venta{c.num_ventas !== 1 ? 's' : ''}</p>
        </div>
        <div>
          <p className="text-xs text-zinc-500">Total abonado</p>
          <p className="font-semibold text-green-700">{fmt(c.total_abonado_periodo)}</p>
          <p className="text-xs text-zinc-400">{abonos.length} abono{abonos.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Abonos detalle */}
      {abonos.length > 0 && (
        <div className="px-4 py-2 space-y-1">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1">Desglose de abonos</p>
          {abonos.map((a, i) => (
            <div key={i} className="flex items-center justify-between py-1 border-b border-zinc-50 last:border-0">
              <span className="text-xs text-zinc-500">{fmtFecha(a.fecha)}</span>
              <span className="text-sm font-semibold text-green-700">{fmt(a.monto)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1 border-t border-zinc-200">
            <span className="text-xs font-bold text-zinc-600">Total abonado</span>
            <span className="text-sm font-bold text-green-800">{fmt(c.total_abonado_periodo)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
